import React, { createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Initial CV state structure
const initialCVState = {
  personalInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    dateOfBirth: '',
    nationality: '',
    linkedIn: '',
    website: '',
    github: '',
    photo: null
  },
  professionalSummary: '',
  workExperience: [],
  education: [],
  skills: {
    technical: [],
    languages: [],
    soft: []
  },
  certifications: [],
  projects: [],
  achievements: [],
  interests: [],
  references: [],
  template: 'modern',
  currentStep: 0,
  isComplete: false
};

// Initial app state
const initialState = {
  // CV data
  cvData: initialCVState,
  
  // UI state
  currentStep: 0,
  isLoading: false,
  error: null,
  
  // Language and direction
  language: 'en',
  direction: 'ltr',
  
  // Form state
  currentQuestion: 0,
  currentSubQuestion: -1,
  isFormComplete: false,
  
  // Upload state
  uploadedFile: null,
  extractedData: null
};

// Helper functions for nested object manipulation
function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  const newObj = JSON.parse(JSON.stringify(obj));
  let current = newObj;
  
  for (let i = 0; i < keys.length - 1; i++) {
    if (!(keys[i] in current)) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  
  current[keys[keys.length - 1]] = value;
  return newObj;
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Action types (kept for compatibility if used elsewhere)
export const ActionTypes = {
  SET_CV_DATA: 'SET_CV_DATA',
  UPDATE_CV_FIELD: 'UPDATE_CV_FIELD',
  ADD_ARRAY_ITEM: 'ADD_ARRAY_ITEM',
  REMOVE_ARRAY_ITEM: 'REMOVE_ARRAY_ITEM',
  UPDATE_ARRAY_ITEM: 'UPDATE_ARRAY_ITEM',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  SET_CURRENT_QUESTION: 'SET_CURRENT_QUESTION',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  RESET_STATE: 'RESET_STATE',
  SET_UPLOADED_FILE: 'SET_UPLOADED_FILE',
  SET_EXTRACTED_DATA: 'SET_EXTRACTED_DATA',
  UPDATE_PERSONAL_INFO: 'UPDATE_PERSONAL_INFO',
  UPDATE_PROFESSIONAL_SUMMARY: 'UPDATE_PROFESSIONAL_SUMMARY',
  ADD_WORK_EXPERIENCE: 'ADD_WORK_EXPERIENCE',
  UPDATE_WORK_EXPERIENCE: 'UPDATE_WORK_EXPERIENCE',
  REMOVE_WORK_EXPERIENCE: 'REMOVE_WORK_EXPERIENCE',
  ADD_EDUCATION: 'ADD_EDUCATION',
  UPDATE_EDUCATION: 'UPDATE_EDUCATION',
  REMOVE_EDUCATION: 'REMOVE_EDUCATION',
  UPDATE_SKILLS: 'UPDATE_SKILLS',
  ADD_CERTIFICATION: 'ADD_CERTIFICATION',
  UPDATE_CERTIFICATION: 'UPDATE_CERTIFICATION',
  REMOVE_CERTIFICATION: 'REMOVE_CERTIFICATION',
  ADD_PROJECT: 'ADD_PROJECT',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  REMOVE_PROJECT: 'REMOVE_PROJECT',
  SET_TEMPLATE: 'SET_TEMPLATE',
  SET_COMPLETE: 'SET_COMPLETE',
  LOAD_CV_DATA: 'LOAD_CV_DATA',
  RESET_CV: 'RESET_CV'
};

// Zustand Store
export const useStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        setCVData: (data) => set((state) => ({ cvData: { ...state.cvData, ...data } })),
        
        updateCVField: (path, value) => set((state) => ({ 
          cvData: setNestedValue(state.cvData, path, value) 
        })),
        
        addArrayItem: (path, item) => set((state) => {
          const currentArray = getNestedValue(state.cvData, path) || [];
          return { cvData: setNestedValue(state.cvData, path, [...currentArray, item]) };
        }),
        
        removeArrayItem: (path, index) => set((state) => {
          const removeArray = getNestedValue(state.cvData, path) || [];
          const filteredArray = removeArray.filter((_, i) => i !== index);
          return { cvData: setNestedValue(state.cvData, path, filteredArray) };
        }),
        
        updateArrayItem: (path, index, updates) => set((state) => {
          const updateArray = getNestedValue(state.cvData, path) || [];
          const updatedArray = updateArray.map((item, i) => 
            i === index ? { ...item, ...updates } : item
          );
          return { cvData: setNestedValue(state.cvData, path, updatedArray) };
        }),
        
        setLanguage: (language) => set({ 
          language, 
          direction: language === 'ar' ? 'rtl' : 'ltr' 
        }),
        
        setCurrentStep: (step) => set({ currentStep: step }),
        
        setCurrentQuestion: (question, subQuestion) => set({ 
          currentQuestion: question, 
          currentSubQuestion: subQuestion || -1 
        }),
        
        setLoading: (loading) => set({ isLoading: loading }),
        
        setError: (error) => set({ error }),
        
        setUploadedFile: (file) => set({ uploadedFile: file }),
        
        setExtractedData: (data) => set((state) => ({ 
          extractedData: data,
          cvData: data ? { ...state.cvData, ...data } : state.cvData 
        })),
        
        resetState: () => set((state) => ({
          ...initialState,
          language: state.language,
          direction: state.direction
        })),
        
        updatePersonalInfo: (data) => set((state) => ({
          cvData: { ...state.cvData, personalInfo: { ...state.cvData.personalInfo, ...data } }
        })),
        
        updateProfessionalSummary: (summary) => set((state) => ({
          cvData: { ...state.cvData, professionalSummary: summary }
        })),
        
        addWorkExperience: () => set((state) => ({
          cvData: {
            ...state.cvData,
            workExperience: [...state.cvData.workExperience, {
              id: Date.now(),
              company: '',
              position: '',
              startDate: '',
              endDate: '',
              current: false,
              description: '',
              location: ''
            }]
          }
        })),
        
        updateWorkExperience: (id, data) => set((state) => ({
          cvData: {
            ...state.cvData,
            workExperience: state.cvData.workExperience.map(exp =>
              exp.id === id ? { ...exp, ...data } : exp
            )
          }
        })),
        
        removeWorkExperience: (id) => set((state) => ({
          cvData: {
            ...state.cvData,
            workExperience: state.cvData.workExperience.filter(exp => exp.id !== id)
          }
        })),
        
        addEducation: () => set((state) => ({
          cvData: {
            ...state.cvData,
            education: [...state.cvData.education, {
              id: Date.now(),
              institution: '',
              degree: '',
              field: '',
              startDate: '',
              endDate: '',
              current: false,
              gpa: '',
              location: '',
              description: ''
            }]
          }
        })),
        
        updateEducation: (id, data) => set((state) => ({
          cvData: {
            ...state.cvData,
            education: state.cvData.education.map(edu =>
              edu.id === id ? { ...edu, ...data } : edu
            )
          }
        })),
        
        removeEducation: (id) => set((state) => ({
          cvData: {
            ...state.cvData,
            education: state.cvData.education.filter(edu => edu.id !== id)
          }
        })),
        
        updateSkills: (skillsData) => set((state) => ({
          cvData: { ...state.cvData, skills: { ...state.cvData.skills, ...skillsData } }
        })),
        
        addCertification: () => set((state) => ({
          cvData: {
            ...state.cvData,
            certifications: [...state.cvData.certifications, {
              id: Date.now(),
              name: '',
              issuer: '',
              date: '',
              expiryDate: '',
              credentialId: '',
              url: ''
            }]
          }
        })),
        
        updateCertification: (id, data) => set((state) => ({
          cvData: {
            ...state.cvData,
            certifications: state.cvData.certifications.map(cert =>
              cert.id === id ? { ...cert, ...data } : cert
            )
          }
        })),
        
        removeCertification: (id) => set((state) => ({
          cvData: {
            ...state.cvData,
            certifications: state.cvData.certifications.filter(cert => cert.id !== id)
          }
        })),
        
        addProject: () => set((state) => ({
          cvData: {
            ...state.cvData,
            projects: [...state.cvData.projects, {
              id: Date.now(),
              name: '',
              description: '',
              technologies: [],
              url: '',
              github: '',
              startDate: '',
              endDate: ''
            }]
          }
        })),
        
        updateProject: (id, data) => set((state) => ({
          cvData: {
            ...state.cvData,
            projects: state.cvData.projects.map(project =>
              project.id === id ? { ...project, ...data } : project
            )
          }
        })),
        
        removeProject: (id) => set((state) => ({
          cvData: {
            ...state.cvData,
            projects: state.cvData.projects.filter(project => project.id !== id)
          }
        })),
        
        setTemplate: (template) => set((state) => ({
          cvData: { ...state.cvData, template }
        })),
        
        setComplete: (isComplete) => set((state) => ({
          cvData: { ...state.cvData, isComplete }
        })),
        
        resetCV: () => set({ cvData: initialCVState }),
        
        loadCVData: (cvData) => set({ cvData })
      }
    }),
    {
      name: 'cvData',
      partialize: (state) => ({ cvData: state.cvData }),
    }
  )
);

// Context Provider (Dummy provider for backward compatibility)
export const CVContext = createContext();

export function CVProvider({ children }) {
  const direction = useStore((state) => state.direction);
  const language = useStore((state) => state.language);

  // Update document direction
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [direction, language]);

  return (
    <CVContext.Provider value={{}}>
      {children}
    </CVContext.Provider>
  );
}

// Hook that maps Zustand to the old Context shape
export function useCV() {
  const store = useStore();
  
  // Reconstruct the state object
  const state = {
    cvData: store.cvData,
    currentStep: store.currentStep,
    isLoading: store.isLoading,
    error: store.error,
    language: store.language,
    direction: store.direction,
    currentQuestion: store.currentQuestion,
    currentSubQuestion: store.currentSubQuestion,
    isFormComplete: store.isFormComplete,
    uploadedFile: store.uploadedFile,
    extractedData: store.extractedData
  };

  return { state, actions: store.actions };
}